import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { getBrokers, getArticles, createBroker, updateBroker, deleteBroker, createArticle, updateArticle, deleteArticle } from '../lib/api';
import { Pencil, Trash2, Plus, Save, X, ChevronDown, ChevronUp, Globe, ExternalLink, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';

const EMPTY_BROKER = {
  name: '', slug: '', short_description: '', full_description: '', founded: '', regulation: '',
  headquarters: '', min_deposit: '', platforms: '', instruments: '', score: 4.5, affiliate_url: '',
  pros: [''], cons: [''], fees: { spreads: '', commission: '', swap_fees: '', inactivity_fee: '' },
  verdict: '', account_types: '', leverage: '', deposit_methods: '', withdrawal_methods: '',
  customer_support: '', education_resources: '', is_featured: true, order: 0,
};

const EMPTY_ARTICLE = {
  title: '', slug: '', excerpt: '', content: '', category: '', image_url: '', reading_time: '', is_published: true, order: 0,
};

function BrokerForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setFee = (k, v) => setForm(prev => ({ ...prev, fees: { ...prev.fees, [k]: v } }));
  const setList = (key, idx, val) => {
    const arr = [...form[key]]; arr[idx] = val; set(key, arr);
  };
  const addToList = (key) => set(key, [...form[key], '']);
  const removeFromList = (key, idx) => set(key, form[key].filter((_, i) => i !== idx));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="broker-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { label: 'Name', key: 'name' }, { label: 'Slug', key: 'slug' },
          { label: 'Founded', key: 'founded' }, { label: 'Regulation', key: 'regulation' },
          { label: 'Headquarters', key: 'headquarters' }, { label: 'Min Deposit', key: 'min_deposit' },
          { label: 'Platforms', key: 'platforms' }, { label: 'Instruments', key: 'instruments' },
          { label: 'Affiliate URL', key: 'affiliate_url' }, { label: 'Account Types', key: 'account_types' },
          { label: 'Leverage', key: 'leverage' }, { label: 'Customer Support', key: 'customer_support' },
          { label: 'Deposit Methods', key: 'deposit_methods' }, { label: 'Withdrawal Methods', key: 'withdrawal_methods' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-600 block mb-1">{f.label}</label>
            <input value={form[f.key]} onChange={e => set(f.key, e.target.value)}
              className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
          </div>
        ))}
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Score (0-5)</label>
          <input type="number" step="0.1" min="0" max="5" value={form.score} onChange={e => set('score', parseFloat(e.target.value) || 0)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Order</label>
          <input type="number" value={form.order} onChange={e => set('order', parseInt(e.target.value) || 0)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Short Description</label>
        <textarea value={form.short_description} onChange={e => set('short_description', e.target.value)} rows={2}
          className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Full Description</label>
        <textarea value={form.full_description} onChange={e => set('full_description', e.target.value)} rows={4}
          className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Verdict</label>
        <textarea value={form.verdict} onChange={e => set('verdict', e.target.value)} rows={3}
          className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Spreads</label>
          <input value={form.fees.spreads} onChange={e => setFee('spreads', e.target.value)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Commission</label>
          <input value={form.fees.commission} onChange={e => setFee('commission', e.target.value)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Swap Fees</label>
          <input value={form.fees.swap_fees} onChange={e => setFee('swap_fees', e.target.value)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Inactivity Fee</label>
          <input value={form.fees.inactivity_fee} onChange={e => setFee('inactivity_fee', e.target.value)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
      </div>
      {['pros', 'cons'].map(key => (
        <div key={key}>
          <label className="text-xs font-medium text-slate-600 block mb-1 capitalize">{key}</label>
          {form[key].map((item, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input value={item} onChange={e => setList(key, i, e.target.value)}
                className="flex-1 rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
              <button onClick={() => removeFromList(key, i)} className="text-red-500 hover:text-red-700 p-1"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => addToList(key)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">+ Add {key === 'pros' ? 'Pro' : 'Con'}</button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-slate-600">Featured</label>
        <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={() => onSave(form)} className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 h-9 px-4 transition-colors" data-testid="save-broker-btn">
          <Save className="w-4 h-4" /> Save
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 h-9 px-4 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

function ArticleForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="article-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { label: 'Title', key: 'title' }, { label: 'Slug', key: 'slug' },
          { label: 'Category', key: 'category' }, { label: 'Reading Time', key: 'reading_time' },
          { label: 'Image URL', key: 'image_url' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-600 block mb-1">{f.label}</label>
            <input value={form[f.key]} onChange={e => set(f.key, e.target.value)}
              className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
          </div>
        ))}
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Order</label>
          <input type="number" value={form.order} onChange={e => set('order', parseInt(e.target.value) || 0)}
            className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Excerpt</label>
        <textarea value={form.excerpt} onChange={e => set('excerpt', e.target.value)} rows={2}
          className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Content (HTML)</label>
        <textarea value={form.content} onChange={e => set('content', e.target.value)} rows={10}
          className="w-full rounded border border-slate-200 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-600 focus:outline-none" />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-slate-600">Published</label>
        <input type="checkbox" checked={form.is_published} onChange={e => set('is_published', e.target.checked)} />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={() => onSave(form)} className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 h-9 px-4 transition-colors" data-testid="save-article-btn">
          <Save className="w-4 h-4" /> Save
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 h-9 px-4 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

const CATEGORIES = ['Education', 'Money Management', 'Technical Analysis', 'Forex Strategies', 'Trading Psychology', 'Market News'];

function QuickArticleForm({ onSave, onCancel, articleCount }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Education');
  const [imageUrl, setImageUrl] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [readingTime, setReadingTime] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const generateSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return alert('Title and content are required.');
    setSaving(true);

    // Auto-convert plain text paragraphs to HTML
    let htmlContent = content;
    if (!content.includes('<')) {
      htmlContent = content.split('\n\n').filter(p => p.trim()).map(p => {
        const trimmed = p.trim();
        if (trimmed.startsWith('## ')) return `<h2>${trimmed.slice(3)}</h2>`;
        if (trimmed.startsWith('### ')) return `<h3>${trimmed.slice(4)}</h3>`;
        if (trimmed.startsWith('- ')) {
          const items = trimmed.split('\n').map(l => `<li>${l.replace(/^-\s*/, '')}</li>`).join('\n');
          return `<ul>\n${items}\n</ul>`;
        }
        return `<p>${trimmed}</p>`;
      }).join('\n\n');
    }

    const wordCount = content.split(/\s+/).length;
    const autoReadingTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;
    const autoExcerpt = content.replace(/<[^>]*>/g, '').replace(/^##+ /gm, '').slice(0, 160).trim() + '...';

    await onSave({
      title: title.trim(),
      slug: generateSlug(title),
      excerpt: excerpt.trim() || autoExcerpt,
      content: htmlContent,
      category,
      image_url: imageUrl.trim(),
      reading_time: readingTime.trim() || autoReadingTime,
      is_published: true,
      order: articleCount + 1,
    });
    setSaving(false);
    setTitle('');
    setContent('');
    setImageUrl('');
    setExcerpt('');
    setReadingTime('');
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 space-y-4" data-testid="quick-article-form">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Quick Article Creator</h3>
          <p className="text-xs text-slate-500 mt-0.5">Fill in all fields. Use ## for H2, ### for H3, and - for bullet points in content. Or paste raw HTML.</p>
        </div>
        <button onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Article Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Understanding Moving Averages"
            className="w-full rounded border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none bg-white" data-testid="quick-title-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Category *</label>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="w-full rounded border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none bg-white" data-testid="quick-category-select">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Reading Time <span className="text-slate-400">(auto if empty)</span></label>
          <input value={readingTime} onChange={e => setReadingTime(e.target.value)} placeholder="e.g., 8 min read"
            className="w-full rounded border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none bg-white" data-testid="quick-reading-time-input" />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Image URL</label>
        <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://images.unsplash.com/photo-..."
          className="w-full rounded border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none bg-white" data-testid="quick-image-url-input" />
        {imageUrl && (
          <div className="mt-2 h-20 w-36 rounded border border-slate-200 overflow-hidden bg-white">
            <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
          </div>
        )}
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Excerpt / Summary <span className="text-slate-400">(auto-generated from content if empty)</span></label>
        <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} rows={2}
          placeholder="Short summary of the article for previews and SEO..."
          className="w-full rounded border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none bg-white" data-testid="quick-excerpt-textarea" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 block mb-1">Content * (plain text with markdown-style headers, or raw HTML)</label>
        <textarea value={content} onChange={e => setContent(e.target.value)} rows={12}
          placeholder={"## Introduction\n\nYour article content here. Each paragraph separated by a blank line will be wrapped in <p> tags.\n\n### Sub-heading\n\nMore content here...\n\n- Bullet point 1\n- Bullet point 2\n- Bullet point 3"}
          className="w-full rounded border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-600 focus:outline-none bg-white font-mono" data-testid="quick-content-textarea" />
      </div>
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 h-9 px-5 transition-colors disabled:opacity-50" data-testid="quick-save-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Publish Article'}
        </button>
        <span className="text-xs text-slate-500">Auto-generates: slug, excerpt, reading time</span>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState('brokers');
  const [brokers, setBrokers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editBroker, setEditBroker] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    const [b, a] = await Promise.all([getBrokers(), getArticles()]);
    setBrokers(b.data);
    setArticles(a.data);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSaveBroker = async (form) => {
    const data = { ...form, pros: form.pros.filter(p => p.trim()), cons: form.cons.filter(c => c.trim()) };
    if (editBroker === 'new') {
      await createBroker(data);
    } else {
      await updateBroker(editBroker, data);
    }
    setEditBroker(null);
    fetchData();
  };

  const handleDeleteBroker = async (slug) => {
    if (window.confirm(`Delete broker "${slug}"?`)) {
      await deleteBroker(slug);
      fetchData();
    }
  };

  const handleSaveArticle = async (form) => {
    if (editArticle === 'new') {
      await createArticle(form);
    } else {
      await updateArticle(editArticle, form);
    }
    setEditArticle(null);
    fetchData();
  };

  const handleDeleteArticle = async (slug) => {
    if (window.confirm(`Delete article "${slug}"?`)) {
      await deleteArticle(slug);
      fetchData();
    }
  };

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Helmet>
        <title>Admin Panel | MarketsReview.org</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="bg-slate-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">Admin Panel</h1>
            <p className="text-sm text-slate-400 mt-1">Manage brokers, articles, and site content</p>
          </div>
          <div className="flex items-center gap-4">
            {user && <span className="text-xs text-slate-400">{user.email}</span>}
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium border border-slate-600 text-slate-300 hover:bg-slate-800 h-9 px-4 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-slate-200 rounded-lg p-1 w-fit">
          {['brokers', 'articles', 'domain', 'export'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
                tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`admin-tab-${t}`}
            >
              {t === 'brokers' ? `Brokers (${brokers.length})` : t === 'articles' ? `Articles (${articles.length})` : t === 'domain' ? 'Domain Setup' : 'Export Data'}
            </button>
          ))}
        </div>

        {/* Brokers Tab */}
        {tab === 'brokers' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-900 font-['Outfit']">Brokers</h2>
              <button
                onClick={() => setEditBroker('new')}
                className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 h-9 px-4 transition-colors"
                data-testid="add-broker-btn"
              >
                <Plus className="w-4 h-4" /> Add Broker
              </button>
            </div>

            {editBroker === 'new' && (
              <div className="mb-6">
                <h3 className="text-base font-medium text-slate-900 mb-3">New Broker</h3>
                <BrokerForm initial={EMPTY_BROKER} onSave={handleSaveBroker} onCancel={() => setEditBroker(null)} />
              </div>
            )}

            <div className="space-y-3">
              {brokers.map(broker => (
                <div key={broker.slug} className="bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [broker.slug]: !p[broker.slug] }))}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-900">{broker.name}</span>
                      <span className="text-xs text-slate-500">/{broker.slug}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{broker.score}/5</span>
                      {broker.is_featured && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Featured</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); setEditBroker(broker.slug); }} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`edit-broker-${broker.slug}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteBroker(broker.slug); }} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-broker-${broker.slug}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expanded[broker.slug] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                  {editBroker === broker.slug && (
                    <div className="px-4 pb-4">
                      <BrokerForm initial={broker} onSave={handleSaveBroker} onCancel={() => setEditBroker(null)} />
                    </div>
                  )}
                  {expanded[broker.slug] && editBroker !== broker.slug && (
                    <div className="px-4 pb-4 text-sm text-slate-600 border-t border-slate-100 pt-3">
                      <p className="mb-2"><strong>Regulation:</strong> {broker.regulation}</p>
                      <p className="mb-2"><strong>Platforms:</strong> {broker.platforms}</p>
                      <p><strong>Description:</strong> {broker.short_description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Articles Tab */}
        {tab === 'articles' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-900 font-['Outfit']">Articles ({articles.length})</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditArticle('quick')}
                  className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 h-9 px-4 transition-colors"
                  data-testid="quick-add-article-btn"
                >
                  <Plus className="w-4 h-4" /> Quick Add
                </button>
                <button
                  onClick={() => setEditArticle('new')}
                  className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 h-9 px-4 transition-colors"
                  data-testid="add-article-btn"
                >
                  <Plus className="w-4 h-4" /> Full Editor
                </button>
              </div>
            </div>

            {/* Quick Add Form - simplified for adding 20 articles/week */}
            {editArticle === 'quick' && (
              <QuickArticleForm
                onSave={async (form) => { await createArticle(form); setEditArticle(null); fetchData(); }}
                onCancel={() => setEditArticle(null)}
                articleCount={articles.length}
              />
            )}

            {editArticle === 'new' && (
              <div className="mb-6">
                <h3 className="text-base font-medium text-slate-900 mb-3">New Article (Full Editor)</h3>
                <ArticleForm initial={EMPTY_ARTICLE} onSave={handleSaveArticle} onCancel={() => setEditArticle(null)} />
              </div>
            )}

            <div className="space-y-3">
              {articles.map(article => (
                <div key={article.slug} className="bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-900">{article.title}</span>
                      <span className="text-xs text-slate-500">/{article.slug}</span>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{article.category}</span>
                      {article.is_published ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Published</span>
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Draft</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditArticle(article.slug)} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`edit-article-${article.slug}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteArticle(article.slug)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-article-${article.slug}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {editArticle === article.slug && (
                    <div className="px-4 pb-4">
                      <ArticleForm initial={article} onSave={handleSaveArticle} onCancel={() => setEditArticle(null)} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Domain Setup Tab */}
        {tab === 'domain' && (
          <DomainSetupTab />
        )}

        {/* Export Tab */}
        {tab === 'export' && (
          <ExportTab />
        )}
      </div>
    </div>
  );
}

function DomainSetupTab() {
  const [guide, setGuide] = useState(null);
  const [subdomainConfig, setSubdomainConfig] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/domain-setup-guide'),
      api.get('/subdomain-config')
    ]).then(([guideRes, subRes]) => {
      setGuide(guideRes.data);
      setSubdomainConfig(subRes.data);
    }).catch(console.error);
  }, []);

  return (
    <div data-testid="domain-setup-tab">
      <h2 className="text-xl font-semibold text-slate-900 font-['Outfit'] mb-6">Domain & Subdomain Configuration</h2>

      {/* DNS Setup Guide */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-blue-600" />
          <h3 className="text-base font-semibold text-slate-900">Namecheap DNS Setup for marketsreview.org</h3>
        </div>
        {guide ? (
          <div className="space-y-4">
            {guide.steps.map(step => (
              <div key={step.step} className="flex gap-4">
                <div className="flex items-center justify-center w-8 h-8 bg-slate-100 rounded-full shrink-0 text-sm font-bold text-slate-700">
                  {step.step}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">{step.title}</h4>
                  <p className="text-sm text-slate-600 mt-0.5">{step.instructions}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Loading guide...</p>
        )}
      </div>

      {/* Subdomain Mappings */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Subdomain to Broker Mappings</h3>
        <p className="text-sm text-slate-600 mb-4">
          When a user visits a broker subdomain (e.g., vantage.marketsreview.org), they will be automatically redirected to the broker's review page.
        </p>
        {subdomainConfig ? (
          <div className="overflow-auto rounded-lg border border-slate-200">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-900 uppercase">Subdomain</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-900 uppercase">Broker</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-900 uppercase">Review Page</th>
                </tr>
              </thead>
              <tbody>
                {subdomainConfig.brokers.map(b => (
                  <tr key={b.subdomain} className="border-b border-slate-200 last:border-0">
                    <td className="px-4 py-3 text-sm">
                      <code className="bg-slate-100 px-2 py-0.5 rounded text-slate-700">{b.subdomain}.marketsreview.org</code>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{b.name}</td>
                    <td className="px-4 py-3 text-sm text-blue-600">/{b.slug}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">Loading...</p>
        )}
      </div>

      {/* SEO Status */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4">SEO & Sitemap</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <span className="text-sm font-medium text-slate-900">Sitemap.xml</span>
              <p className="text-xs text-slate-500">Auto-generated from all brokers and articles</p>
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
              View <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <span className="text-sm font-medium text-slate-900">Robots.txt</span>
              <p className="text-xs text-slate-500">Allows indexing, blocks /admin and /api</p>
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/robots.txt`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
              View <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <span className="text-sm font-medium text-green-800">SEO Meta Tags Active</span>
            <p className="text-xs text-green-600 mt-0.5">Open Graph, Twitter Cards, JSON-LD structured data (Organization, WebSite, Review, Article, BreadcrumbList schemas) are active on all pages.</p>
          </div>
        </div>
      </div>
    </div>
  );
}


const ADMIN_SECRET = process.env.REACT_APP_ADMIN_SECRET || '';

function ExportTab() {
  const [loading, setLoading] = useState({});
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/export/all', { headers: { 'x-admin-secret': ADMIN_SECRET } })
      .then(res => {
        const cols = res.data.collections;
        setStats({
          brokers: cols.brokers.count,
          articles: cols.articles.count,
          legal_pages: cols.legal_pages.count,
          total: cols.brokers.count + cols.articles.count + cols.legal_pages.count,
          exported_at: res.data.exported_at,
        });
      })
      .catch(() => {});
  }, []);

  const download = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCollection = async (name) => {
    setLoading(prev => ({ ...prev, [name]: true }));
    try {
      const endpoint = name === 'legal_pages' ? 'legal-pages' : name;
      const res = await api.get(`/export/${endpoint}`, { headers: { 'x-admin-secret': ADMIN_SECRET } });
      const timestamp = new Date().toISOString().slice(0, 10);
      download(res.data, `marketsreview_${name}_${timestamp}.json`);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
    setLoading(prev => ({ ...prev, [name]: false }));
  };

  const exportAll = async () => {
    setLoading(prev => ({ ...prev, all: true }));
    try {
      const res = await api.get('/export/all', { headers: { 'x-admin-secret': ADMIN_SECRET } });
      const timestamp = new Date().toISOString().slice(0, 10);
      download(res.data, `marketsreview_full_backup_${timestamp}.json`);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
    setLoading(prev => ({ ...prev, all: false }));
  };

  const collections = [
    { key: 'brokers', label: 'Brokers', desc: 'All broker profiles, scores, fees, pros/cons, and affiliate links' },
    { key: 'articles', label: 'Articles', desc: 'All educational guides and articles with full HTML content' },
    { key: 'legal_pages', label: 'Legal Pages', desc: 'Privacy Policy, Terms, Affiliate Disclosure, Risk Disclaimer' },
  ];

  return (
    <div data-testid="export-tab">
      <h2 className="text-xl font-semibold text-slate-900 font-['Outfit'] mb-2">Export & Backup</h2>
      <p className="text-sm text-slate-600 mb-8">Download your database as JSON files for backup or migration to another MongoDB instance.</p>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Brokers', value: stats.brokers },
            { label: 'Articles', value: stats.articles },
            { label: 'Legal Pages', value: stats.legal_pages },
            { label: 'Total Documents', value: stats.total },
          ].map(s => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-900 font-['Outfit']">{s.value}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Full Backup */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6" data-testid="full-backup-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Full Database Backup</h3>
            <p className="text-sm text-slate-600 mt-1">
              Downloads all {stats?.total || '...'} documents across all 3 collections in a single JSON file.
              Use this file with the migration script to import into any MongoDB instance.
            </p>
          </div>
          <button
            onClick={exportAll}
            disabled={loading.all}
            className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 h-11 px-6 transition-colors disabled:opacity-50 shrink-0"
            data-testid="export-all-btn"
          >
            <Save className="w-4 h-4" />
            {loading.all ? 'Exporting...' : 'Download Full Backup'}
          </button>
        </div>
      </div>

      {/* Individual Collections */}
      <h3 className="text-base font-semibold text-slate-900 mb-4">Export Individual Collections</h3>
      <div className="space-y-3">
        {collections.map(col => (
          <div key={col.key} className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`export-card-${col.key}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{col.label}</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{stats?.[col.key] || '...'} docs</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{col.desc}</p>
            </div>
            <button
              onClick={() => exportCollection(col.key)}
              disabled={loading[col.key]}
              className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 h-9 px-4 transition-colors disabled:opacity-50 shrink-0"
              data-testid={`export-${col.key}-btn`}
            >
              <Save className="w-3.5 h-3.5" />
              {loading[col.key] ? 'Exporting...' : 'Download'}
            </button>
          </div>
        ))}
      </div>

      {/* Migration Instructions */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mt-8">
        <h3 className="text-base font-semibold text-slate-900 mb-3">How to Import Into Another MongoDB</h3>
        <p className="text-sm text-slate-600 mb-3">Use the migration script included in the project:</p>
        <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-green-400 font-mono whitespace-pre">
{`# Install pymongo if needed
pip install pymongo

# Import the backup file into your MongoDB
python3 scripts/migrate.py import \\
  --mongo-url mongodb://your-server:27017 \\
  --db-name marketsreview \\
  --file marketsreview_full_backup_YYYY-MM-DD.json`}
          </pre>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          The script supports 3 modes: <strong>overwrite</strong> (drop and re-insert), <strong>merge</strong> (upsert by slug), or <strong>skip</strong> (keep existing).
        </p>
      </div>
    </div>
  );
}
